// @ts-strict-ignore
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { OverlayManagerService } from '@app/_shared/service/overlay-manager.service';
import { ThemeService } from '@app/_shared/service/theme.service';
import { SUPPORTED_LANGUAGES } from '@config';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TieredMenu } from 'primeng/tieredmenu';
import { TooltipModule } from 'primeng/tooltip';
import { map } from 'rxjs';

interface ExtendedMenuItem extends MenuItem {
  active?: () => boolean;
}

@Component({
  selector: 'sb-dev-tools',
  templateUrl: './dev-tools.component.html',
  imports: [CommonModule, ButtonModule, TieredMenu, TieredMenu, TooltipModule, FontAwesomeModule, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevToolsComponent implements OnInit {
  private themeService = inject(ThemeService);
  private translocoService = inject(TranslocoService);
  private overlayManager = inject(OverlayManagerService);

  private readonly currentLang = toSignal(
    this.translocoService.langChanges$.pipe(
      takeUntilDestroyed(),
      map((lang) => lang),
    ),
  );

  private readonly currentTheme = toSignal(
    this.themeService.themeMode$.pipe(
      takeUntilDestroyed(),
      map((mode) => mode),
    ),
  );

  private devMenu = viewChild<TieredMenu>('devMenu');

  ngOnInit(): void {
    this.loadPreferencesFromLocalStorage();
  }

  toggleDevMenu(event: MouseEvent) {
    event.stopImmediatePropagation();
    this.overlayManager.toggle(this.devMenu(), event);
  }

  private loadPreferencesFromLocalStorage(): void {
    const savedLanguage = localStorage.getItem('language_preference');
    const savedTheme = localStorage.getItem('theme_preference');

    if (savedLanguage) {
      this.translocoService.setActiveLang(savedLanguage);
    }

    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      this.themeService.setTheme(savedTheme);
    }
  }

  devMenuItems = computed<ExtendedMenuItem[]>(() => {
    const items: ExtendedMenuItem[] = [
      {
        header: true,
        label: 'Language Settings',
      },
    ];

    SUPPORTED_LANGUAGES.forEach((lang) => {
      items.push({
        id: `language-${lang.code}`,
        label: lang.label,
        active: () => this.currentLang() === lang.code,
        command: () => this.changeLanguage(lang.code),
      });
    });

    items.push(
      {
        header: true,
        label: 'Theme Settings',
      },
      {
        id: 'theme-light',
        label: 'Light Theme',
        active: () => this.currentTheme() === 'light',
        command: () => this.setTheme('light'),
      },
      {
        id: 'theme-dark',
        label: 'Dark Theme',
        active: () => this.currentTheme() === 'dark',
        command: () => this.setTheme('dark'),
      },
      {
        separator: true,
      },
      {
        id: 'reset-preferences',
        label: 'Reset Preferences',
        command: () => this.resetPreferences(),
      },
    );

    return items;
  });

  changeLanguage(langCode: string): void {
    this.translocoService.setActiveLang(langCode);
    localStorage.setItem('language_preference', langCode);
  }

  setTheme(theme: 'light' | 'dark'): void {
    this.themeService.setTheme(theme);
    localStorage.setItem('theme_preference', theme);
  }

  resetPreferences(): void {
    localStorage.removeItem('language_preference');
    localStorage.removeItem('theme_preference');

    // Reset to default language and theme
    this.translocoService.setActiveLang('da');
    this.themeService.setTheme('light');
  }

  isDevToolsEnabled(): boolean {
    const enableDevTools = localStorage.getItem('enable_devtools');
    return enableDevTools === 'true';
  }
}
